import React, { useState, useCallback } from 'react';
import { TodaysScheduleItem } from './types';
import { Avatar } from '../../components/avatar';
import { Badge } from '../../components/badge';
import { formatShiftTime } from '../../shared/utils/dateUtils';
import useApiService from '../../hooks/useApiService';
import ShiftModal from '../../components/shiftDetailPage';
import { IClinicianProfile, IShiftTimes } from '../../interfaces/types';
import { semanticColors } from '../../theme';
import { getDisplayStatus } from '../../shared/utils/get-status-color';
import {
  ClockIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';

interface TodaysScheduleProps {
  openings: TodaysScheduleItem[];
  className?: string;
}

const TodaysSchedule: React.FC<TodaysScheduleProps> = ({ openings, className = '' }) => {
  const api = useApiService();
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [selectedOpening, setSelectedOpening] = useState<TodaysScheduleItem | undefined>(undefined);
  const [clinicianProfile, setClinicianProfile] = useState<IClinicianProfile | null>(null);
  const [loadingClinicianId, setLoadingClinicianId] = useState<string | null>(null);

  const formatTime = (time?: string | null) => {
    if (!time) return '--:--';
    return formatShiftTime(time);
  };

  const formatDate = (time?: string | null): string => {
    if (!time) return 'Invalid Date';
    try {
      const date = new Date(time);
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: '2-digit', 
        year: 'numeric' 
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const fetchClinicianProfile = useCallback(
    async (clinicianId: string) => {
      try {
        setLoadingClinicianId(clinicianId);
        const response = await api.get<IClinicianProfile>(`/clinicians/clinician-profile?clinicianId=${clinicianId}&isFacilityUser=true`);
        
        setClinicianProfile(response.data);
      } catch (error) {
        setClinicianProfile(null);
      } finally {
        setLoadingClinicianId(null);
      }
    },
    [api],
  );

  const handleClinicianClick = useCallback(
    async (opening: TodaysScheduleItem) => {
      setSelectedOpening(opening);
      if (opening.clinician_id) {
        await fetchClinicianProfile(opening.clinician_id);
      } else {
        setClinicianProfile(null);
      }
      setModalOpen(true);
    },
    [fetchClinicianProfile],
  );

  const onUpdateShift = () => {
    // Shift update handler
  };

  // Set status styling based on opening status with modern gradients (matching weekly schedule)
  const getStatusStyle = (status: string | undefined, applicationsCount = 0) => {
    // Handle undefined or null status
    if (!status) {
      return 'bg-gradient-to-r from-gray-100 to-slate-100 text-gray-800 border border-gray-200/50';
    }

    // Normalize to handle variants like "In Progress"
    const normalizedStatus = status.replace(/\s+/g, '_').toUpperCase();

    // Special case: if status is ACTIVE and there are applications, show "Applicants Applied" styling
    if (normalizedStatus === 'ACTIVE' && applicationsCount > 0) {
      return 'bg-gradient-to-r from-blue-100 to-sky-100 text-blue-800 border border-blue-200/50';
    }
    
    switch (normalizedStatus) {
      case 'CONFIRMED':
        return 'bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 border border-green-200/50';
      case 'ACTIVE':
        return 'bg-gradient-to-r from-blue-100 to-sky-100 text-blue-800 border border-blue-200/50';
      case 'CANCELLED':
        return 'bg-gradient-to-r from-red-100 to-rose-100 text-red-800 border border-red-200/50';
      case 'SELECTED':
        return 'bg-gradient-to-r from-amber-100 to-yellow-100 text-amber-800 border border-amber-200/50';
      case 'COMPLETED':
        return 'bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 border border-green-200/50';
      case 'EXPIRED':
        return 'bg-gradient-to-r from-orange-100 to-amber-100 text-orange-800 border border-orange-200/50';
      case 'NO_SHOW':
        return 'bg-gradient-to-r from-red-100 to-rose-100 text-red-800 border border-red-200/50';
      case 'MISSED_CLOCK_IN':
      case 'MISSED_CLOCK_OUT':
        return 'bg-gradient-to-r from-orange-100 to-red-100 text-orange-800 border border-orange-200/50';
      case 'CLOCKED_IN':
        return 'bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 border border-green-200/50';
      case 'UNRESOLVED':
        return 'bg-gradient-to-r from-orange-100 to-amber-100 text-orange-800 border border-orange-200/50';
      case 'IN_PROGRESS':
        return 'bg-gradient-to-r from-blue-100 to-sky-100 text-blue-800 border border-blue-200/50';
      case 'WORKED':
        return 'bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 border border-green-200/50';
      case 'WITHDRAWN':
        return 'bg-gradient-to-r from-red-100 to-rose-100 text-red-800 border border-red-200/50';
      default:
        return 'bg-gradient-to-r from-gray-100 to-slate-100 text-gray-800 border border-gray-200/50';
    }
  };

  // Function to get display status based on opening status and applications (matching weekly schedule)
  const getDisplayStatusForOpening = (status: string | undefined, applicationsCount: number) => {
    // Handle undefined or null status
    if (!status) {
      return 'Unknown';
    }

    const normalizedStatus = status.replace(/\s+/g, '_').toUpperCase();
    
    // Special case: if status is ACTIVE and there are applications, show "Applicants Applied"
    if (normalizedStatus === 'ACTIVE' && applicationsCount > 0) {
      return 'Applicants Applied';
    }

    // Handle additional display statuses not covered by getDisplayStatus
    if (normalizedStatus === 'IN_PROGRESS') return 'In Progress';
    if (normalizedStatus === 'WORKED') return 'Worked';
    
    return getDisplayStatus(status);
  };


  return (
    <div 
      className={`rounded-2xl border shadow-lg ${className}`}
      style={{ 
        backgroundColor: semanticColors.card.background,
        borderColor: semanticColors.card.border,
        boxShadow: semanticColors.card.shadow,
      }}
    >
      {/* Header - Compact Desktop */}
      <div 
        className="border-b px-4 py-3 sm:px-5 sm:py-4 lg:px-4 lg:py-2"
        style={{ 
          backgroundColor: semanticColors.background.tertiary,
          borderColor: semanticColors.card.border,
        }}
      >
        <div>
          <h3 className="text-lg font-bold sm:text-xl lg:text-base" style={{ color: semanticColors.text.primary }}>
            Today's Schedule
          </h3>
        </div>
      </div>

      {/* Content - Compact Desktop */}
      <div className="p-3 sm:p-4 lg:p-3">
        {openings.length > 0 ? (
          <>
            {/* Desktop Table View */}
            <div className="hidden md:block overflow-hidden rounded-lg border" style={{ borderColor: semanticColors.card.border }}>
              {/* Table Header - Compact Desktop */}
              <div 
                className="px-3 py-2 sm:px-4 sm:py-3 lg:px-3 lg:py-2"
                style={{ backgroundColor: semanticColors.background.tertiary }}
              >
                <div className="grid grid-cols-5 gap-2 text-left text-xs font-semibold uppercase tracking-wider sm:gap-3 lg:gap-2" style={{ color: semanticColors.text.secondary }}>
                  <div>SCHEDULED TIME</div>
                  <div>POSITION</div>
                  <div>CLINICIAN NAME</div>
                  <div>RELIABILITY</div>
                  <div>STATUS</div>
                </div>
              </div>

              {/* Table Body */}
              <div className="divide-y" style={{ borderColor: semanticColors.card.border }}>
                {openings.map((opening) => {
                  const initials = opening.firstname && opening.lastname 
                    ? `${opening.firstname[0]}${opening.lastname[0]}`.toUpperCase()
                    : 'U';

                  return (
                    <div
                      key={opening.opening_id}
                      className="px-3 py-2 transition-all duration-200 hover:bg-gray-50 sm:px-4 sm:py-3 lg:px-3 lg:py-2"
                    >
                      <div className="grid grid-cols-5 gap-2 items-center sm:gap-3 lg:gap-2">
                        {/* Scheduled Time Column */}
                        <div>
                          <div className="text-xs font-medium sm:text-sm lg:text-xs" style={{ color: semanticColors.text.primary }}>
                            {formatTime(opening.start_time)} - {formatTime(opening.end_time)}
                          </div>
                          <div className="text-xs mt-1" style={{ color: semanticColors.text.secondary }}>
                            {formatDate(opening.start_time)}
                          </div>
                        </div>

                        {/* Position Column */}
                        <div>
                          <Badge color="blue" className="text-xs">
                            {opening.position_name}
                          </Badge>
                        </div>

                        {/* Clinician Name Column */}
                        <div 
                          className={`${opening.clinician_id ? 'cursor-pointer hover:underline' : ''}`}
                          onClick={() => opening.clinician_id && handleClinicianClick(opening)}
                        >
                          <div className="flex items-center space-x-2">
                            <Avatar initials={initials} size="md" />
                            <div className="flex-1 min-w-0">
                              <div className="text-xs font-semibold truncate sm:text-sm lg:text-xs" style={{ color: semanticColors.text.primary }}>
                                {opening.firstname && opening.lastname 
                                  ? `${opening.firstname} ${opening.lastname}`
                                  : 'Unassigned'
                                }
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Reliability Column */}
                        <div className="text-xs sm:text-sm lg:text-xs" style={{ color: semanticColors.text.primary }}>
                          {opening.reliability ? `${opening.reliability}%` : 'N/A'}
                        </div>

                        {/* Status Column */}
                        <div>
                          <span
                            className={`inline-flex items-center rounded-full px-3 py-1.5 text-xs font-semibold shadow-md ring-1 ring-white/20 backdrop-blur-sm ${getStatusStyle(opening.opening_status, opening.applications_count || 0)}`}
                          >
                            {getDisplayStatusForOpening(opening.opening_status, opening.applications_count || 0)}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Mobile/Tablet Card View */}
            <div className="block space-y-4 md:hidden">
              {openings.map((opening) => {
                const initials = opening.firstname && opening.lastname 
                  ? `${opening.firstname[0]}${opening.lastname[0]}`.toUpperCase()
                  : 'U';

                return (
                  <div
                    key={opening.opening_id}
                    className="rounded-lg border p-4 space-y-3"
                    style={{ 
                      backgroundColor: semanticColors.card.background,
                      borderColor: semanticColors.card.border 
                    }}
                  >
                    {/* Time and Position */}
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-sm" style={{ color: semanticColors.text.primary }}>
                          {formatTime(opening.start_time)} - {formatTime(opening.end_time)}
                        </div>
                        <div className="text-xs mt-1" style={{ color: semanticColors.text.secondary }}>
                          {formatDate(opening.start_time)}
                        </div>
                      </div>
                      <Badge color="blue" className="text-xs">
                        {opening.position_name}
                      </Badge>
                    </div>

                    {/* Clinician Info */}
                    <div 
                      className={`flex items-center space-x-3 ${opening.clinician_id ? 'cursor-pointer' : ''}`}
                      onClick={() => opening.clinician_id && handleClinicianClick(opening)}
                    >
                      <Avatar initials={initials} size="lg" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold truncate" style={{ color: semanticColors.text.primary }}>
                          {opening.firstname && opening.lastname 
                            ? `${opening.firstname} ${opening.lastname}`
                            : 'Unassigned'
                          }
                        </div>
                        <div className="flex items-center space-x-2 mt-1">
                          <span
                            className={`inline-flex items-center rounded-full px-3 py-1.5 text-xs font-semibold shadow-md ring-1 ring-white/20 backdrop-blur-sm ${getStatusStyle(opening.opening_status, opening.applications_count || 0)}`}
                          >
                            {getDisplayStatusForOpening(opening.opening_status, opening.applications_count || 0)}
                          </span>
                          <span className="text-xs" style={{ color: semanticColors.text.secondary }}>
                            Reliability: {opening.reliability ? `${opening.reliability}%` : 'N/A'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* View Details Button */}
                    <div className="flex justify-end">
                      <button
                        onClick={() => handleClinicianClick(opening)}
                        className="inline-flex items-center px-3 py-2 text-xs font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 transition-colors duration-200"
                      >
                        <ChevronRightIcon className="w-3 h-3 mr-1" />
                        View Shift Details
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <div className="py-12 text-center">
            <div 
              className="mx-auto flex size-16 items-center justify-center rounded-full"
              style={{ backgroundColor: semanticColors.background.tertiary }}
            >
              <ClockIcon className="size-8" style={{ color: semanticColors.text.tertiary }} />
            </div>
            <p className="mt-2 text-lg font-semibold" style={{ color: semanticColors.text.primary }}>
              No shifts scheduled today
            </p>
            <p className="mt-1 text-sm" style={{ color: semanticColors.text.secondary }}>
              Check back tomorrow for updates
            </p>
          </div>
        )}
      </div>

      {selectedOpening && (
        <>
          {loadingClinicianId && (
            <div className="fixed inset-0 z-[1001] flex items-center justify-center bg-black/50 backdrop-blur-sm">
              <div className="rounded-2xl bg-white p-8 shadow-2xl">
                <div className="text-center">
                  <div className="mx-auto mb-4 size-12 animate-spin rounded-full border-4 border-gray-200 border-t-blue-600"></div>
                  <h3 className="text-lg font-semibold text-gray-900">Loading Clinician Profile...</h3>
                  <p className="mt-2 text-sm text-gray-600">Please wait while we fetch the clinician details</p>
                </div>
              </div>
            </div>
          )}
          <ShiftModal
            open={modalOpen}
            handleClose={() => {
              setModalOpen(false);
              setSelectedOpening(undefined);
              setClinicianProfile(null);
              setLoadingClinicianId(null);
            }}
            shift={{
              id: selectedOpening.opening_id,
              shift_id: selectedOpening.shift_id,
              date: selectedOpening.start_time,
              start_time: selectedOpening.start_time,
              end_time: selectedOpening.end_time,
              status: selectedOpening.opening_status,
              clinician_id: selectedOpening.clinician_id || undefined,
              firstname: selectedOpening.firstname || undefined,
              lastname: selectedOpening.lastname || undefined,
              email: '',
              base_rate: 0,
              rate_margin: 0,
              immutable_clock_in: null,
              immutable_clock_out: null,
              description: '',
            }}
            clinicianProfile={clinicianProfile}
            onUpdateShift={onUpdateShift}
          />
        </>
      )}
    </div>
  );
};

export default TodaysSchedule;